import React from 'react';

type Toast = {
  id: number;
  type: 'success' | 'error' | 'info';
  message: string;
};

export function useToastController() {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const add = (type: Toast['type'], message: string) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  };
  return { toasts, add };
}

export function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

type ToastContextType = {
  add: (type: 'success' | 'error' | 'info', message: string) => void;
};

const ToastContext = React.createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const controller = useToastController();

  React.useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent;
      const { type, message } = (custom.detail || {}) as { type: 'success' | 'error' | 'info'; message: string };
      if (type && message) controller.add(type, message);
    };
    window.addEventListener('toast', handler as EventListener);
    return () => window.removeEventListener('toast', handler as EventListener);
  }, [controller]);
  return (
    <ToastContext.Provider value={{ add: controller.add }}>
      {children}
      <ToastContainer toasts={controller.toasts} />
    </ToastContext.Provider>
  );
}

export default ToastProvider;

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  const labels: Record<Toast['type'], string> = {
    success: 'Sucesso',
    error: 'Erro',
    info: 'Info',
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`min-w-[260px] max-w-sm rounded-md shadow-lg border px-4 py-3 text-sm flex items-center gap-2 ${
            t.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : t.type === 'error'
              ? 'bg-red-50 border-red-200 text-red-900'
              : 'bg-gray-50 border-gray-200 text-gray-900'
          }`}
        >
          <span className="font-semibold">{labels[t.type]}</span>
          <span className="opacity-80">•</span>
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}
